// types
import type { PalettesProps } from '@ant-design/colors'
import type { SimplePaletteColorOptions } from '@mui/material/styles'
import { darken, lighten } from '@mui/material/styles'
import appColors from 'themes/colors'
import { ThemeMode } from 'types/config'
import type { PaletteThemeProps } from 'types/theme'

function getSimpleColorOptions(color: string, percent: number, contrastText: string): SimplePaletteColorOptions {
  return {
    lighter: lighten(color, percent * 0.02), // 2x percent lighter
    100: lighten(color, percent * 0.015), // 1.5x percent lighter
    200: lighten(color, percent * 0.01), // 1x percent lighter
    light: lighten(color, percent * 0.005), // 0.5x percent lighter
    400: darken(color, percent * 0.005), // 0.5x percent darker
    main: color, // Base color
    dark: darken(color, percent * 0.01), // 1x percent darker
    700: darken(color, percent * 0.015), // 1.5x percent darker
    darker: darken(color, percent * 0.02), // 2x percent darker
    900: darken(color, percent * 0.025), // 2.5x percent darker
    contrastText,
  }
}

interface ProgressColors {
  progress: string
  remaining: string
  excess: string
}

function getLightModeProgressColors(baseColor: string): ProgressColors {
  return {
    progress: baseColor, // Use base color for progress
    remaining: lighten(baseColor, 0.8), // Much lighter for remaining
    excess: darken(baseColor, 0.3), // Much darker for excess
  }
}

function getDarkModeProgressColors(baseColor: string): ProgressColors {
  return {
    progress: baseColor, // Use base color for progress
    remaining: darken(baseColor, 0.3), // Darker for remaining in dark mode
    excess: lighten(baseColor, 0.4), // Much lighter for excess in dark mode
  }
}

function ThemePurcent(colors: PalettesProps, mode: ThemeMode): PaletteThemeProps {
  const { grey } = colors

  let primaryColors = getSimpleColorOptions(appColors.caribbeanBlue, 25, appColors.white)
  let secondaryColors = getSimpleColorOptions(appColors.lightGray, 21, appColors.black)
  let errorColors = getSimpleColorOptions(appColors.crayolaRed, 20, appColors.white)
  let warningColors = getSimpleColorOptions(appColors.pear, 20, appColors.black)
  let infoColors = getSimpleColorOptions(appColors.turquoise, 10, appColors.white)
  let successColors = getSimpleColorOptions(darken(appColors.kellyGreen, 0.1), 10, appColors.black)

  if (mode === ThemeMode.DARK) {
    primaryColors = getSimpleColorOptions(darken(appColors.turquoise, 0.1), 20, appColors.white)
    secondaryColors = getSimpleColorOptions(darken(appColors.mediumGray, 0.5), 3, appColors.white)
    errorColors = getSimpleColorOptions(lighten(appColors.crayolaRed, 0.25), 20, appColors.white)
    warningColors = getSimpleColorOptions(appColors.pear, 20, appColors.black)
    infoColors = getSimpleColorOptions(appColors.caribbeanBlue, 20, appColors.white)
    successColors = getSimpleColorOptions(darken(appColors.kellyGreen, 0.1), 20, appColors.white)
  }

  return {
    primary: primaryColors,
    secondary: secondaryColors,
    error: errorColors,
    warning: warningColors,
    info: infoColors,
    success: successColors,
    grey: {
      0: grey[0],
      50: grey[1],
      100: grey[2],
      200: grey[3],
      300: grey[4],
      400: grey[5],
      500: grey[6],
      600: grey[7],
      700: grey[8],
      800: grey[9],
      900: grey[10],
      A50: grey[15],
      A100: grey[11],
      A200: grey[12],
      A400: grey[13],
      A700: grey[14],
      A800: grey[16],
    },
  }
}

export default ThemePurcent
export { getLightModeProgressColors, getDarkModeProgressColors }
